import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { FileIcon } from '../../components/attachments/FileIcon';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import {
  Badge,
  Button,
  Card,
  ConfirmationDialog,
  DateInput,
  Label,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from '../../components/ui';
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ExternalLinkIcon,
  ListTreeIcon,
  PlusIcon,
  RefreshIcon,
  SaveIcon,
  SpinnerIcon,
  TrashIcon,
  WizardIcon,
} from '../../components/ui/Icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { useNavigateBack, getLocationReturnPath } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import { normalizePaginatedListResponse } from '../../services/paginatedList';
import {
  applyDocumentPostingRules,
  deleteDocument,
  scanDocument,
  submitDocumentForApproval,
  updateDocument,
} from '../../services/mutations';
import { VesselSelect } from '../../components/ui/dropdowns';
import { CoaAccountPickerModal } from '../accounting/shared/CoaAccountPickerModal';
import { DocumentLine, DocumentPostingRole, DocumentStatus, useDocument, useDocumentFileTypes } from './useData';
import { TeachAmountStyle, TeachPostingWizard } from './TeachPostingWizard';

type AccLookup = { id: number; code: string; name: string; isActive?: boolean; combLevel?: string | null };
type Department = { id: number; name?: string; departmentName?: string };
type BasicUser = { id: number; userDisplayName?: string | null; userFirstName: string; userLastName: string };
type LastReturnPayload = {
  comments?: string | null;
  at?: string | null;
  byUserId?: number | null;
  label?: string | null;
};

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  return normalizePaginatedListResponse<T>(data as any, 1, 2000).data;
}

function userLabel(user?: BasicUser | null) {
  if (!user) return null;
  return user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim();
}

function companyLabel(c: AccLookup) {
  return c.name;
}

function accountLabel(a: AccLookup) {
  return a.name || a.combLevel || a.code;
}

function hasExtractedAmounts(payload: Record<string, unknown> | null | undefined) {
  if (!payload) return false;
  const keys = ['totalAmount', 'netAmount', 'vatAmount', 'lines'];
  return keys.some((k) => {
    const v = payload[k];
    if (Array.isArray(v)) return v.length > 0;
    return v != null && v !== '';
  });
}

function payloadString(payload: Record<string, unknown>, key: string): string {
  const v = payload[key];
  return typeof v === 'string' ? v : '';
}

type ExtractedLine = { description: string; amount: number | null };

function extractedLines(payload: Record<string, unknown>): ExtractedLine[] {
  if (!Array.isArray(payload.lines)) return [];
  return payload.lines.flatMap((row) => {
    if (!row || typeof row !== 'object') return [];
    const r = row as { description?: unknown; amount?: unknown };
    const description = typeof r.description === 'string' ? r.description.trim() : '';
    const amount = typeof r.amount === 'number' && Number.isFinite(r.amount) ? r.amount : null;
    if (!description && amount == null) return [];
    return [{ description, amount }];
  });
}

const EXTRA_INVOICE_FIELDS = [
  ['poNumber', 'PO / order no.'],
  ['dueDate', 'Due date'],
  ['vatNumber', 'VAT / tax ID'],
  ['paymentTerms', 'Payment terms'],
  ['buyerName', 'Bill-to / buyer'],
] as const;

type AppliedPostingRule = { name?: string; code?: string; id?: number };
type PostingRuleLookup = {
  matched?: boolean;
  fileTypeId?: number | null;
  supplierName?: string | null;
  ruleName?: string | null;
  ruleCode?: string | null;
};

function noticeAfterScan(scanned: {
  fileTypeId?: number | null;
  payload?: Record<string, unknown> | null;
}): string {
  const payload = scanned.payload && typeof scanned.payload === 'object' ? scanned.payload : {};
  const postingEnabled = payload._postingEnabled === true;
  const applied =
    payload._appliedPostingRule && typeof payload._appliedPostingRule === 'object'
      ? (payload._appliedPostingRule as AppliedPostingRule)
      : null;
  const lookup =
    payload._postingRuleLookup && typeof payload._postingRuleLookup === 'object'
      ? (payload._postingRuleLookup as PostingRuleLookup)
      : null;
  const ruleLabel = applied?.name || applied?.code || lookup?.ruleName || lookup?.ruleCode;
  const supplier =
    (typeof payload.supplierName === 'string' && payload.supplierName.trim()) ||
    (typeof lookup?.supplierName === 'string' && lookup.supplierName.trim()) ||
    '';
  const fileTypeId = scanned.fileTypeId ?? lookup?.fileTypeId ?? null;

  if (!postingEnabled) {
    if (lookup?.matched && ruleLabel) {
      return `AI scan completed — extracted invoice data. A posting rule (“${ruleLabel}”) is available when you prepare accounting.`;
    }
    return 'AI scan completed — review the extracted invoice data. Accounting / journal is optional.';
  }

  if (applied || lookup?.matched) {
    return ruleLabel
      ? `AI scan completed — matched posting rule “${ruleLabel}” for this file type${supplier ? ` / ${supplier}` : ''} and applied journal lines.`
      : `AI scan completed — a posting rule matched this file type${supplier ? ` / supplier` : ''} and journal lines were applied.`;
  }
  if (fileTypeId) {
    return supplier
      ? `AI scan completed — no posting rule for this file type and supplier (${supplier}). Set accounts on the journal lines, then Remember posting.`
      : 'AI scan completed — no posting rule for this file type. Set accounts on the journal lines, then Remember posting.';
  }
  return 'AI scan completed — set a file type to look up posting rules, or Remember posting.';
}

function inferAmountStyle(lines: DocumentLine[], payload: Record<string, unknown>): TeachAmountStyle | null {
  const stored =
    (payload._postingAmountStyle as string | undefined) ||
    (payload._taughtPostingRule as { amountStyle?: string } | undefined)?.amountStyle;
  if (stored === 'TOTAL_VAT' || stored === 'FULL_AMOUNT' || stored === 'SCAN_LINES') {
    return stored;
  }
  const roles = lines.map((l) => l.role).filter(Boolean) as DocumentPostingRole[];
  if (!roles.length) return null;
  if (roles.includes('VAT')) return 'TOTAL_VAT';
  const expenseCount = roles.filter((r) => r === 'EXPENSE').length;
  if (expenseCount > 1) return 'SCAN_LINES';
  if (roles.includes('EXPENSE') && roles.includes('PAYABLE')) return 'FULL_AMOUNT';
  return null;
}

function round2(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function amountOf(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return 0;
}

function accountForRole(lines: DocumentLine[], role: DocumentPostingRole): number | null {
  return lines.find((l) => l.role === role && l.accountId != null)?.accountId ?? null;
}

function accountForNarrative(lines: DocumentLine[], narrative: string, fallback: number | null): number | null {
  const key = narrative.trim().toLowerCase();
  if (!key) return fallback;
  const match = lines.find(
    (l) => l.role === 'EXPENSE' && (l.narrative || '').trim().toLowerCase() === key && l.accountId != null,
  );
  return match?.accountId ?? fallback;
}

function companyFromLines(lines: DocumentLine[]): number | null {
  return lines.find((l) => l.companyId != null)?.companyId ?? null;
}

function currencyFromLines(lines: DocumentLine[], payload: Record<string, unknown>): number | null {
  const fromLine = lines.find((l) => l.currencyId != null)?.currencyId;
  if (fromLine != null) return fromLine;
  return Number.isInteger(Number(payload.currencyId)) ? Number(payload.currencyId) : null;
}

/** Rebuild journal lines for a posting style using payload amounts and existing role→account mapping. */
function rebuildJournalLinesForStyle(
  style: TeachAmountStyle,
  lines: DocumentLine[],
  payload: Record<string, unknown>,
): DocumentLine[] {
  const companyId = companyFromLines(lines);
  const currencyId = currencyFromLines(lines, payload);
  const expenseAccountId = accountForRole(lines, 'EXPENSE');
  const vatAccountId = accountForRole(lines, 'VAT');
  const payableAccountId = accountForRole(lines, 'PAYABLE');

  const vat = amountOf(payload.vatAmount ?? payload.vat ?? payload.taxAmount);
  const gross = amountOf(payload.totalAmount);
  const netExplicit = amountOf(payload.netAmount ?? payload.net ?? payload.taxableAmount);
  const net =
    netExplicit > 0
      ? netExplicit
      : vat > 0 && gross > vat
        ? round2(gross - vat)
        : gross;
  const payable = gross > 0 ? gross : round2(net + vat);

  const scanLines = Array.isArray(payload.lines)
    ? (payload.lines as Array<{ description?: string | null; amount?: number | null }>)
    : [];

  const narrative =
    (typeof payload.description === 'string' && payload.description) ||
    (typeof payload.summary === 'string' && payload.summary) ||
    (typeof payload.narrative === 'string' && payload.narrative) ||
    '';

  const base = (partial: DocumentLine): DocumentLine => ({
    companyId,
    currencyId,
    debitLocal: 0,
    creditLocal: 0,
    narrative: '',
    ...partial,
  });

  if (style === 'FULL_AMOUNT') {
    const expenseAmt = payable > 0 ? payable : net;
    return [
      base({
        role: 'EXPENSE',
        accountId: expenseAccountId,
        debitLocal: expenseAmt,
        creditLocal: 0,
        narrative: narrative || 'EXPENSE',
      }),
      base({
        role: 'PAYABLE',
        accountId: payableAccountId,
        debitLocal: 0,
        creditLocal: expenseAmt,
        narrative: 'PAYABLE',
      }),
    ].filter((l) => Number(l.debitLocal) > 0 || Number(l.creditLocal) > 0);
  }

  if (style === 'SCAN_LINES') {
    const rows =
      scanLines.length > 0
        ? scanLines
            .map((row) => ({
              description: row.description || 'EXPENSE',
              amount: amountOf(row.amount),
            }))
            .filter((row) => row.amount > 0)
        : net > 0 || payable > 0
          ? [{ description: narrative || 'EXPENSE', amount: net > 0 ? net : payable }]
          : [];

    const expenseLines = rows.map((row) =>
      base({
        role: 'EXPENSE',
        accountId: accountForNarrative(lines, row.description, expenseAccountId),
        debitLocal: row.amount,
        creditLocal: 0,
        narrative: row.description,
      }),
    );

    const debitSum = round2(expenseLines.reduce((s, l) => s + Number(l.debitLocal || 0), 0));

    return [
      ...expenseLines,
      base({
        role: 'PAYABLE',
        accountId: payableAccountId,
        debitLocal: 0,
        creditLocal: debitSum,
        narrative: 'PAYABLE',
      }),
    ].filter((l) => Number(l.debitLocal) > 0 || Number(l.creditLocal) > 0);
  }

  // TOTAL_VAT
  const out: DocumentLine[] = [
    base({
      role: 'EXPENSE',
      accountId: expenseAccountId,
      debitLocal: net > 0 ? net : payable,
      creditLocal: 0,
      narrative: narrative || 'EXPENSE',
    }),
  ];
  if (vat > 0) {
    out.push(
      base({
        role: 'VAT',
        accountId: vatAccountId,
        debitLocal: vat,
        creditLocal: 0,
        narrative: 'VAT',
      }),
    );
  }
  const debitSum = round2(out.reduce((s, l) => s + Number(l.debitLocal || 0), 0));
  out.push(
    base({
      role: 'PAYABLE',
      accountId: payableAccountId,
      debitLocal: 0,
      creditLocal: debitSum,
      narrative: 'PAYABLE',
    }),
  );
  return out.filter((l) => Number(l.debitLocal) > 0 || Number(l.creditLocal) > 0);
}

const POSTING_STYLES: Array<{ value: TeachAmountStyle; label: string; hint: string }> = [
  { value: 'TOTAL_VAT', label: 'Total + VAT', hint: 'Expense · VAT · Payable' },
  { value: 'FULL_AMOUNT', label: 'Full amount', hint: 'Expense · Payable' },
  { value: 'SCAN_LINES', label: 'Line by line', hint: 'One debit per scan line' },
];

const ROLE_OPTIONS: Array<{ value: DocumentPostingRole; label: string }> = [
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'VAT', label: 'VAT' },
  { value: 'PAYABLE', label: 'Payable' },
  { value: 'RECEIVABLE', label: 'Receivable' },
  { value: 'OTHER', label: 'Other' },
];

function roleBadgeClass(role: DocumentPostingRole | null | undefined, isDark: boolean) {
  if (isDark) {
    switch (role) {
      case 'EXPENSE':
        return 'bg-teal-400/15 text-teal-200';
      case 'VAT':
        return 'bg-violet-400/15 text-violet-200';
      case 'PAYABLE':
        return 'bg-amber-400/15 text-amber-200';
      case 'RECEIVABLE':
        return 'bg-sky-400/15 text-sky-200';
      default:
        return 'bg-slate-400/15 text-slate-200';
    }
  }
  switch (role) {
    case 'EXPENSE':
      return 'bg-teal-600/10 text-teal-800';
    case 'VAT':
      return 'bg-violet-600/10 text-violet-800';
    case 'PAYABLE':
      return 'bg-amber-600/10 text-amber-900';
    case 'RECEIVABLE':
      return 'bg-sky-600/10 text-sky-800';
    default:
      return 'bg-slate-500/10 text-slate-700';
  }
}

const variants: Record<DocumentStatus, 'default' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'default',
  IN_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

const statusLabel: Record<DocumentStatus, string> = {
  DRAFT: 'Draft',
  IN_APPROVAL: 'In approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const Detail: React.FC = () => {
  const id = Number(useParams<{ id: string }>().id);
  const location = useLocation();
  const goBack = useNavigateBack('/documents', getLocationReturnPath(location.state));
  const { data: doc, isLoading, mutate } = useDocument(id);
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { setTitleOverride, setRightActions } = useTopbar();

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [fileTypeId, setFileTypeId] = useState<number | ''>('');
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [lines, setLines] = useState<DocumentLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanNotice, setScanNotice] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [teachOpen, setTeachOpen] = useState(false);
  const [teachStyle, setTeachStyle] = useState<TeachAmountStyle | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<'pdf' | 'image' | 'other' | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: fileTypes = [] } = useDocumentFileTypes();
  const { data: departmentsRaw } = useSWR('/departments?limit=500', fetcher);
  const { data: usersRaw } = useSWR('/users?limit=500', fetcher);
  const { data: docTypesRaw } = useSWR('/acc-document-types?page=1&limit=200', fetcher);
  const { data: currenciesRaw } = useSWR('/currencies', fetcher);
  const { data: companiesRaw } = useSWR('/acc-companies?page=1&limit=500', fetcher);
  // Omit page/limit so the API returns the full posting COA (pagination is capped at 200).
  const { data: accountsRaw } = useSWR('/acc-accounts?postingOnly=true', fetcher);

  const docTypes = useMemo(() => asList<AccLookup>(docTypesRaw).filter((d) => d.isActive !== false), [docTypesRaw]);
  const currencies = useMemo(() => {
    const list = Array.isArray(currenciesRaw)
      ? (currenciesRaw as AccLookup[])
      : asList<AccLookup>(currenciesRaw);
    return list.filter((c) => c.isActive !== false);
  }, [currenciesRaw]);
  const companies = useMemo(() => asList<AccLookup>(companiesRaw).filter((c) => c.isActive !== false), [companiesRaw]);
  const accounts = useMemo(() => asList<AccLookup>(accountsRaw), [accountsRaw]);
  const departments = useMemo(() => {
    if (Array.isArray(departmentsRaw)) return departmentsRaw as Department[];
    return asList<Department>(departmentsRaw);
  }, [departmentsRaw]);
  const users = useMemo(() => asList<BasicUser>(usersRaw), [usersRaw]);

  const docTypeOptions = useMemo(
    () => docTypes.map((d) => ({ value: d.id, label: d.name })),
    [docTypes],
  );
  const currencyOptions = useMemo(
    () => currencies.map((c) => ({ value: c.id, label: c.name })),
    [currencies],
  );
  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        value: d.id,
        label: d.name || d.departmentName || `Department #${d.id}`,
      })),
    [departments],
  );
  const fileTypeOptions = useMemo(
    () =>
      fileTypes
        .filter((t) => t.isActive !== false)
        .map((t) => ({ value: t.id, label: t.name || t.code })),
    [fileTypes],
  );

  useEffect(() => {
    if (doc) {
      setTitle(doc.title);
      setSummary(doc.summary || '');
      setDepartmentId(doc.departmentId ?? '');
      setFileTypeId(doc.fileTypeId ?? '');
      setPayload(doc.payload || {});
      setLines(doc.lines || []);
    }
  }, [doc]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    if (!doc?.storageKey) {
      setPreviewUrl(null);
      setPreviewKind(null);
      setPreviewBusy(false);
      return;
    }
    setPreviewBusy(true);
    (async () => {
      try {
        const response = await api.get(`/documents/${doc.id}/file`, { responseType: 'blob' });
        if (cancelled) return;
        const mime = (doc.mimeType || response.headers['content-type'] || '').toLowerCase();
        const blob = new Blob([response.data], { type: mime || response.data.type });
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreviewUrl(objectUrl);
        if (mime.includes('pdf')) setPreviewKind('pdf');
        else if (mime.startsWith('image/')) setPreviewKind('image');
        else setPreviewKind('other');
      } catch {
        if (!cancelled) {
          setPreviewUrl(null);
          setPreviewKind('other');
        }
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.id, doc?.storageKey, doc?.mimeType]);

  useEffect(() => {
    const state = location.state as { scanError?: string; justScanned?: boolean } | null;
    if (!state) return;
    if (state.scanError) {
      setError(state.scanError);
      setScanNotice(null);
      window.history.replaceState({}, window.document.title);
      return;
    }
    if (state.justScanned) {
      setError(null);
      if (doc) {
        setScanNotice(noticeAfterScan(doc));
        window.history.replaceState({}, window.document.title);
      }
      // If doc is still loading, leave justScanned in history until doc arrives.
    }
  }, [location.state, doc]);

  useEffect(() => {
    setTitleOverride(doc?.title || (Number.isFinite(id) ? `Document #${id}` : 'Document'));
    return () => {
      setTitleOverride(undefined);
      setRightActions(null);
    };
  }, [doc?.title, id, setTitleOverride, setRightActions]);

  useEffect(() => {
    if (!doc) {
      setRightActions(null);
      return;
    }
    setRightActions(<Badge variant={variants[doc.status]}>{statusLabel[doc.status]}</Badge>);
    return () => setRightActions(null);
  }, [doc, setRightActions]);

  const debitTotal = useMemo(
    () => round2(lines.reduce((s, l) => s + amountOf(l.debitLocal), 0)),
    [lines],
  );
  const creditTotal = useMemo(
    () => round2(lines.reduce((s, l) => s + amountOf(l.creditLocal), 0)),
    [lines],
  );
  const balanced = Math.abs(debitTotal - creditTotal) < 0.005;
  const currencyLabel =
    currencies.find((c) => c.id === Number(payload.currencyId))?.code ||
    (typeof payload.currencyCode === 'string' ? payload.currencyCode : '') ||
    '';
  const invoiceLines = useMemo(() => extractedLines(payload), [payload]);
  const extraInvoiceFields = EXTRA_INVOICE_FIELDS.filter(([key]) => payloadString(payload, key).trim());

  if (isLoading) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3 p-16', colors.textMuted)}>
        <SpinnerIcon className="h-6 w-6" />
        <p className="text-sm">Loading document…</p>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="p-6">
        <p className={cn('text-sm', colors.textMuted)}>Document not found.</p>
        <button type="button" onClick={goBack} className="mt-3 inline-block text-sm text-indigo-600 hover:underline">
          ← Back to documents
        </button>
      </div>
    );
  }

  const draft = doc.status === 'DRAFT';
  const postingEnabled =
    payload._postingEnabled === true ||
    Boolean(doc.accDocumentId) ||
    Boolean(payload._appliedPostingRule) ||
    Boolean(payload._taughtPostingRule) ||
    (draft && lines.some((l) => l.accountId != null));
  const needsTeach =
    draft &&
    postingEnabled &&
    hasExtractedAmounts(payload) &&
    !(payload && typeof payload._appliedPostingRule === 'object' && payload._appliedPostingRule);
  const lastReturn: LastReturnPayload | null =
    draft && doc.payload && typeof doc.payload._lastReturn === 'object' && doc.payload._lastReturn
      ? (doc.payload._lastReturn as LastReturnPayload)
      : null;
  const lastReturnByUser = lastReturn?.byUserId ? users.find((u) => u.id === lastReturn.byUserId) : undefined;
  const showNotices = Boolean(
    error ||
      scanNotice ||
      needsTeach ||
      lastReturn ||
      (postingEnabled && doc.payload?._appliedPostingRule),
  );
  const errMsg = (e: any) =>
    (Array.isArray(e?.response?.data?.message) ? e.response.data.message.join(', ') : e?.response?.data?.message) ||
    e?.message ||
    'Request failed';

  const save = async () => {
    const nextPayload = {
      ...(postingEnabled ? { ...payload, _postingEnabled: true } : payload),
      summary: summary || undefined,
    };
    if (nextPayload !== payload) setPayload(nextPayload);
    await updateDocument(id, {
      title,
      summary: summary || undefined,
      departmentId: departmentId === '' ? null : Number(departmentId),
      fileTypeId: fileTypeId === '' ? null : Number(fileTypeId),
      payload: nextPayload,
      lines,
    });
    await mutate();
  };

  const changeLine = (index: number, key: keyof DocumentLine, value: string | number | null) =>
    setLines(
      lines.map((line, i) =>
        i === index
          ? {
              ...line,
              [key]:
                key === 'role'
                  ? (value as DocumentPostingRole | null)
                  : ['companyId', 'accountId', 'debitLocal', 'creditLocal'].includes(key)
                    ? value === '' || value == null
                      ? null
                      : Number(value)
                    : value,
            }
          : line,
      ),
    );

  const openTeach = (style?: TeachAmountStyle) => {
    setTeachStyle(style);
    setTeachOpen(true);
  };

  const enablePosting = async () => {
    const scanLineCount = extractedLines(payload).filter((l) => (l.amount ?? 0) > 0).length;
    const useScanLines = scanLineCount > 1;
    const nextLines = useScanLines ? rebuildJournalLinesForStyle('SCAN_LINES', lines, payload) : lines;
    const nextPayload = {
      ...payload,
      _postingEnabled: true,
      ...(useScanLines ? { _postingAmountStyle: 'SCAN_LINES' as const } : {}),
    };
    setPayload(nextPayload);
    if (useScanLines) setLines(nextLines);
    await updateDocument(id, {
      title,
      summary: summary || undefined,
      departmentId: departmentId === '' ? null : Number(departmentId),
      fileTypeId: fileTypeId === '' ? null : Number(fileTypeId),
      payload: nextPayload,
      lines: nextLines,
    });
    if (fileTypeId) {
      try {
        await applyDocumentPostingRules(id);
        setScanNotice('Accounting enabled — posting rules applied to journal lines.');
      } catch {
        setScanNotice('Accounting enabled — set accounts on journal lines, or Remember posting.');
      }
    } else {
      setScanNotice('Accounting enabled — select a file type, then apply Rules or Remember posting.');
    }
    await mutate();
  };

  const applyLineStyle = (style: TeachAmountStyle) => {
    const nextLines = rebuildJournalLinesForStyle(style, lines, payload);
    if (!nextLines.length) {
      setError('Cannot rebuild lines — check net / VAT / payable amounts on the header.');
      return;
    }
    setLines(nextLines);
    setPayload({
      ...payload,
      _postingEnabled: true,
      _postingAmountStyle: style,
    });
    setError(null);
    setScanNotice(
      `Journal lines updated to ${POSTING_STYLES.find((s) => s.value === style)?.label || style}.`,
    );
  };

  const currentStyle = inferAmountStyle(lines, payload);

  const deleteMessage =
    doc.status === 'DRAFT'
      ? `Delete draft “${doc.title}”?`
      : `Delete “${doc.title}” (${statusLabel[doc.status]})? This also removes any linked approval.`;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setAmountField = (field: 'netAmount' | 'vatAmount' | 'totalAmount', value: number | null) => {
    setPayload({ ...payload, [field]: value });
  };

  return (
    <div className={cn('flex h-full min-h-0 w-full flex-col overflow-hidden', colors.background)}>
      <TeachPostingWizard
        open={teachOpen}
        onClose={() => {
          setTeachOpen(false);
          setTeachStyle(undefined);
        }}
        documentId={id}
        payload={payload}
        lines={lines}
        fileTypeLabel={fileTypes.find((t) => t.id === fileTypeId)?.name || doc.fileType?.name}
        initialAmountStyle={teachStyle || currentStyle || undefined}
        ruleOnly={!draft}
        companies={companies}
        accounts={accounts}
        docTypes={docTypes}
        onSaved={async () => {
          setScanNotice(
            draft
              ? 'Posting remembered — journal lines applied and rule saved for next time.'
              : 'Business rule saved — future drafts with this file type / supplier can reuse it.',
          );
          await mutate();
        }}
      />

      <ConfirmationDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete document"
        message={deleteMessage}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={busy}
        onConfirm={async () => {
          setBusy(true);
          setError(null);
          try {
            await deleteDocument(id);
            goBack();
          } catch (e) {
            setError(errMsg(e));
            setBusy(false);
            setDeleteOpen(false);
          }
        }}
      />

      {/* Top bar */}
      <div
        className={cn(
          'sticky top-0 z-20 shrink-0 border-b px-4 py-3 backdrop-blur md:px-6',
          colors.border,
          colors.headerBg,
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button type="button" variant="ghost" size="icon" onClick={goBack} aria-label="Back" title="Back">
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className={cn('truncate text-base font-semibold tracking-tight md:text-lg', colors.text)}>
                  {doc.title || `Document #${id}`}
                </h1>
                <Badge variant={variants[doc.status]}>{statusLabel[doc.status]}</Badge>
              </div>
              <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                {[doc.originalFilename, doc.fileType?.name || fileTypes.find((t) => t.id === fileTypeId)?.name]
                  .filter(Boolean)
                  .join(' · ') || 'Invoice review'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {draft && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={busy}
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      const scanned = await scanDocument(id);
                      await mutate();
                      setScanNotice(noticeAfterScan(scanned));
                    })
                  }
                >
                  <WizardIcon className="h-4 w-4" />
                  Scan
                </Button>
                {postingEnabled ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      loading={busy}
                      disabled={busy || !fileTypeId}
                      title={!fileTypeId ? 'Select a file type first' : undefined}
                      onClick={() => openTeach()}
                    >
                      Remember
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      loading={busy}
                      disabled={busy || !fileTypeId}
                      onClick={() =>
                        run(async () => {
                          await save();
                          await applyDocumentPostingRules(id);
                          await mutate();
                        })
                      }
                    >
                      <RefreshIcon className="h-4 w-4" />
                      Rules
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    loading={busy}
                    disabled={busy}
                    onClick={() => run(enablePosting)}
                  >
                    Prepare accounting
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" loading={busy} disabled={busy} onClick={() => run(save)}>
                  <SaveIcon className="h-4 w-4" />
                  Save
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  loading={busy}
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await save();
                      await submitDocumentForApproval(id);
                      await mutate();
                    })
                  }
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  {lastReturn ? 'Resubmit for approval' : 'Submit'}
                </Button>
              </>
            )}
            {!draft && postingEnabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={busy}
                disabled={busy || !fileTypeId}
                title={
                  !fileTypeId
                    ? 'Document needs a file type to remember posting'
                    : 'Save a business rule from this document’s accounts (does not change submitted lines)'
                }
                onClick={() => openTeach()}
              >
                Remember
              </Button>
            )}
            {!draft && doc.approvalId && (
              <Link to={`/approvals/${doc.approvalId}`}>
                <Button type="button" variant="outline" size="sm">
                  Open approval
                </Button>
              </Link>
            )}
            {doc.accDocumentId && (
              <Link to={`/accounting/journals/${doc.accDocumentId}`}>
                <Button type="button" variant="outline" size="sm">
                  Journal #{doc.accDocumentId}
                </Button>
              </Link>
            )}
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setDeleteOpen(true)}>
              <TrashIcon className={cn('h-4 w-4', isDark ? 'text-red-400' : 'text-red-600')} />
            </Button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4">
          {showNotices && (
            <div className="space-y-2">
              {error && (
                <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.error)}>
                  {error}
                </div>
              )}
              {scanNotice && !error && (
                <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.success)}>
                  {scanNotice}
                </div>
              )}
              {needsTeach && !error && (
                <div
                  className={cn(
                    'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
                    colors.border,
                    colors.warning,
                  )}
                >
                  <span>
                    {fileTypeId
                      ? `No posting rule matched this file type${
                          typeof payload.supplierName === 'string' && payload.supplierName.trim()
                            ? ` / ${payload.supplierName.trim()}`
                            : ''
                        }. Set accounts on the journal lines, then Remember once.`
                      : 'No posting rule yet — select a file type (and optional supplier), set accounts, then Remember once.'}
                  </span>
                  <Button type="button" size="sm" variant="outline" onClick={() => openTeach()} disabled={!fileTypeId}>
                    <WizardIcon className="h-4 w-4" />
                    Remember posting
                  </Button>
                </div>
              )}
              {lastReturn && !error && (
                <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.warning)}>
                  <p className="font-medium">
                    Returned for changes
                    {(lastReturnByUser || lastReturn.byUserId) &&
                      ` by ${userLabel(lastReturnByUser) || `user #${lastReturn.byUserId}`}`}
                    {lastReturn.at && !Number.isNaN(new Date(lastReturn.at).getTime())
                      ? ` on ${new Date(lastReturn.at).toLocaleString()}`
                      : ''}
                  </p>
                  {lastReturn.comments && <p className="mt-1 italic">"{lastReturn.comments}"</p>}
                </div>
              )}
              {doc.payload &&
                typeof doc.payload._appliedPostingRule === 'object' &&
                doc.payload._appliedPostingRule && (
                  <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.info)}>
                    Rule:{' '}
                    <span className="font-semibold">
                      {(doc.payload._appliedPostingRule as { name?: string; code?: string }).name ||
                        (doc.payload._appliedPostingRule as { code?: string }).code}
                    </span>
                  </div>
                )}
            </div>
          )}

          {/* Workspace: preview | details */}
          <div className={cn('grid gap-4', previewOpen && 'xl:grid-cols-12')}>
            {/* Attachment preview */}
            <Card
              className={cn(
                'overflow-hidden rounded-2xl p-0 shadow-sm',
                previewOpen && 'xl:col-span-5 xl:sticky xl:top-[4.5rem] xl:self-start',
              )}
            >
              <div className={cn('flex items-center justify-between gap-3 px-4 py-3.5 md:px-5', previewOpen && 'border-b', colors.border)}>
                <button
                  type="button"
                  onClick={() => setPreviewOpen((open) => !open)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                  aria-expanded={previewOpen}
                >
                  <span
                    className={cn(
                      'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border transition',
                      colors.border,
                      colors.textMuted,
                    )}
                    aria-hidden
                  >
                    <ChevronDownIcon className={cn('h-4 w-4 transition-transform', !previewOpen && '-rotate-90')} />
                  </span>
                  <div className="min-w-0">
                    <h2 className={cn('text-sm font-semibold', colors.text)}>Attachment</h2>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {[doc.originalFilename, formatFileSize(doc.sizeBytes)].filter(Boolean).join(' · ') || 'No file'}
                    </p>
                  </div>
                </button>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Open attachment"
                    aria-label="Open attachment"
                    className={cn(
                      'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition',
                      colors.border,
                      colors.textMuted,
                      colors.interactiveHover,
                    )}
                  >
                    <ExternalLinkIcon className="h-4 w-4" />
                  </a>
                )}
              </div>
              {previewOpen && (
                <div
                  className={cn(
                    'relative flex min-h-[320px] items-center justify-center md:min-h-[420px] xl:min-h-[calc(100vh-14rem)]',
                    colors.headerBg,
                  )}
                >
                  {previewBusy ? (
                    <div className={cn('flex flex-col items-center gap-2 text-sm', colors.textMuted)}>
                      <SpinnerIcon className="h-5 w-5" />
                      Loading preview…
                    </div>
                  ) : previewUrl && previewKind === 'pdf' ? (
                    <iframe title="Document preview" src={previewUrl} className="absolute inset-0 h-full w-full border-0" />
                  ) : previewUrl && previewKind === 'image' ? (
                    <img src={previewUrl} alt={doc.originalFilename || 'Document'} className="max-h-full max-w-full object-contain p-4" />
                  ) : doc.storageKey ? (
                    <div className="flex flex-col items-center gap-3 px-6 text-center">
                      <div className={cn('flex h-20 w-20 items-center justify-center rounded-2xl border shadow-sm', colors.border, colors.surface)}>
                        <FileIcon fileName={doc.originalFilename} size="md" />
                      </div>
                      <p className={cn('text-sm font-medium', colors.text)}>{doc.originalFilename || 'Attachment'}</p>
                      <p className={cn('text-xs', colors.textMuted)}>Preview not available — open the file instead</p>
                      {previewUrl && (
                        <a href={previewUrl} target="_blank" rel="noreferrer" className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
                          Open file
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className={cn('px-6 text-center text-sm', colors.textMuted)}>No attachment on this document.</div>
                  )}
                </div>
              )}
            </Card>

            <div className={cn('flex min-w-0 flex-col gap-4', previewOpen && 'xl:col-span-7')}>
              {/* Amounts strip */}
              <section
                className={cn(
                  'overflow-hidden rounded-2xl border shadow-sm',
                  colors.border,
                  colors.surface,
                )}
              >
                <div
                  className={cn(
                    'grid divide-y sm:grid-cols-[8.5rem_8.5rem_10rem_minmax(0,1fr)] sm:divide-x sm:divide-y-0 md:grid-cols-[9.5rem_9.5rem_11rem_minmax(0,1fr)]',
                    colors.divide,
                  )}
                >
                  {(
                    [
                      { key: 'netAmount' as const, label: 'Net', accent: colors.text },
                      { key: 'vatAmount' as const, label: 'VAT', accent: colors.primaryFg },
                      { key: 'totalAmount' as const, label: 'Payable', accent: colors.primaryFg },
                    ] as const
                  ).map((item) => (
                    <div key={item.key} className="min-w-0 overflow-visible px-3 py-3 md:px-4">
                      <div className="flex items-center justify-between gap-1">
                        <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                          {item.label}
                        </p>
                        {currencyLabel && item.key === 'totalAmount' && (
                          <span className={cn('shrink-0 text-[11px] font-medium', colors.textMuted)}>{currencyLabel}</span>
                        )}
                      </div>
                      {draft ? (
                        <NumberInput
                          value={amountOf(payload[item.key])}
                          allowDecimals
                          showClearButton={false}
                          className={cn(
                            'mt-1 w-full min-w-0 border-0 bg-transparent px-0 text-xl font-semibold tabular-nums shadow-none md:text-2xl',
                            item.accent,
                          )}
                          onChange={(e) => setAmountField(item.key, e.target.value)}
                        />
                      ) : (
                        <p className={cn('mt-1 truncate text-xl font-semibold tabular-nums md:text-2xl', item.accent)}>
                          {amountOf(payload[item.key]).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                  <div className="flex min-w-0 flex-col justify-center gap-2 px-4 py-3 md:px-5">
                    <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                      Classification
                    </p>
                    <div className="flex min-w-0 flex-nowrap items-center gap-2">
                      {draft ? (
                        <>
                          <div className="min-w-0 flex-1 basis-0">
                            <Select
                              value={fileTypeId}
                              options={fileTypeOptions}
                              placeholder="File type"
                              showClearButton
                              onChange={(e) => setFileTypeId(e.target.value === '' ? '' : Number(e.target.value))}
                            />
                          </div>
                          <div className="min-w-0 flex-1 basis-0">
                            <Select
                              value={departmentId}
                              options={departmentOptions}
                              placeholder="Department"
                              showClearButton
                              onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <span className={cn('rounded-lg border px-2.5 py-1 text-xs font-medium', colors.border)}>
                            {doc.fileType?.name || '—'}
                          </span>
                          <span className={cn('rounded-lg border px-2.5 py-1 text-xs font-medium', colors.border)}>
                            {doc.department?.name || doc.department?.departmentName || '—'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Invoice panel */}
              <Card className="overflow-hidden rounded-2xl p-0 shadow-sm">
                <div className={cn('border-b px-5 py-4', colors.border)}>
                  <div className="flex items-start gap-3">
                    <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', colors.info)}>
                      <DocumentTextIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h2 className={cn('text-sm font-semibold', colors.text)}>Invoice</h2>
                      <p className={cn('mt-0.5 text-xs', colors.textMuted)}>Creditor, dates, and details</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 px-5 py-4">
                  {draft ? (
                    <>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="doc-title">Title</Label>
                          <TextInput
                            id="doc-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            showClearButton={false}
                            placeholder="Document title"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <TextInput
                            value={String(payload.description ?? '')}
                            showClearButton={false}
                            onChange={(e) => setPayload({ ...payload, description: e.target.value })}
                            placeholder="Journal narrative"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="doc-summary">Summary</Label>
                        <Textarea
                          id="doc-summary"
                          value={summary}
                          onChange={(e) => setSummary(e.target.value.slice(0, 2000))}
                          rows={6}
                          maxLength={2000}
                          showClearButton={false}
                          showCharacterCount
                          placeholder="What this invoice is for — goods/services, main line items, vessel, PO, period"
                          className="min-h-[8rem] resize-y leading-5"
                        />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        <div className="sm:col-span-2 lg:col-span-3 xl:col-span-2">
                          <Label>Creditor</Label>
                          <TextInput
                            value={String(payload.supplierName ?? '')}
                            showClearButton={false}
                            onChange={(e) => setPayload({ ...payload, supplierName: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Invoice no.</Label>
                          <TextInput
                            value={String(payload.invoiceNumber ?? '')}
                            showClearButton={false}
                            onChange={(e) => setPayload({ ...payload, invoiceNumber: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Currency</Label>
                          <Select
                            value={payload.currencyId != null && payload.currencyId !== '' ? Number(payload.currencyId) : ''}
                            options={currencyOptions}
                            placeholder="Currency"
                            showClearButton
                            onChange={(e) => {
                              const nextId = e.target.value === '' ? undefined : Number(e.target.value);
                              const cur = currencies.find((c) => c.id === nextId);
                              setPayload({ ...payload, currencyId: nextId, currencyCode: cur?.code });
                              setLines(lines.map((l) => ({ ...l, currencyId: nextId ?? null })));
                            }}
                          />
                        </div>
                        <div>
                          <Label>Document date</Label>
                          <DateInput
                            value={String(payload.documentDate ?? '')}
                            showClearButton
                            showQuickFill={false}
                            showFormatHint={false}
                            onChange={(e) => {
                              const v = e.target.value;
                              setPayload({
                                ...payload,
                                documentDate: v,
                                postingDate: payload.postingDate || v,
                              });
                            }}
                          />
                        </div>
                        <div>
                          <Label>Vessel</Label>
                          <VesselSelect
                            value={
                              Number.isInteger(Number(payload.vesselId)) && Number(payload.vesselId) > 0
                                ? String(payload.vesselId)
                                : ''
                            }
                            includeIds={
                              Number.isInteger(Number(payload.vesselId)) && Number(payload.vesselId) > 0
                                ? [Number(payload.vesselId)]
                                : undefined
                            }
                            showClearButton
                            onChange={(e) => {
                              const v = e.target.value;
                              setPayload({
                                ...payload,
                                vesselId: v === '' ? undefined : Number(v),
                              });
                            }}
                          />
                          {!payload.vesselId && payloadString(payload, 'vesselName').trim() ? (
                            <p className={cn('mt-1 text-xs', colors.textMuted)}>
                              Scanned: {payloadString(payload, 'vesselName')}
                            </p>
                          ) : null}
                        </div>
                        {postingEnabled && (
                          <>
                            <div>
                              <Label>Posting date</Label>
                              <DateInput
                                value={String(payload.postingDate ?? '')}
                                showClearButton
                                showQuickFill={false}
                                showFormatHint={false}
                                onChange={(e) => setPayload({ ...payload, postingDate: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label>Journal type</Label>
                              <Select
                                value={
                                  payload.documentTypeId != null && payload.documentTypeId !== ''
                                    ? Number(payload.documentTypeId)
                                    : ''
                                }
                                options={docTypeOptions}
                                placeholder="Journal type"
                                showClearButton
                                onChange={(e) =>
                                  setPayload({
                                    ...payload,
                                    documentTypeId: e.target.value === '' ? undefined : Number(e.target.value),
                                  })
                                }
                              />
                            </div>
                          </>
                        )}
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {EXTRA_INVOICE_FIELDS.map(([key, label]) => (
                          <div key={key}>
                            <Label>{label}</Label>
                            {key === 'dueDate' ? (
                              <DateInput
                                value={payloadString(payload, key)}
                                showClearButton
                                showQuickFill={false}
                                showFormatHint={false}
                                onChange={(e) =>
                                  setPayload({ ...payload, [key]: e.target.value || undefined })
                                }
                              />
                            ) : (
                              <TextInput
                                value={payloadString(payload, key)}
                                showClearButton={false}
                                onChange={(e) => setPayload({ ...payload, [key]: e.target.value })}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      {invoiceLines.length > 0 && (
                        <div>
                          <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                            Line items
                          </p>
                          <ul className={cn('mt-2 divide-y rounded-lg border', colors.border)}>
                            {invoiceLines.map((line, i) => (
                              <li key={i} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                                <span className={cn('min-w-0', colors.text)}>{line.description || '—'}</span>
                                {line.amount != null && (
                                  <span className={cn('shrink-0 tabular-nums', colors.text)}>
                                    {currencyLabel ? `${currencyLabel} ` : ''}
                                    {line.amount.toFixed(2)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <dt className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                          Summary
                        </dt>
                        <dd className={cn('mt-1 whitespace-pre-wrap text-sm leading-6', colors.text)}>
                          {summary || '—'}
                        </dd>
                      </div>
                      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {[
                          ['Creditor', String(payload.supplierName ?? '—')],
                          ['Invoice no.', String(payload.invoiceNumber ?? '—')],
                          ['Document date', String(payload.documentDate ?? '—')],
                          [
                            'Vessel',
                            payloadString(payload, 'vesselName').trim() ||
                              (Number.isInteger(Number(payload.vesselId)) && Number(payload.vesselId) > 0
                                ? `Vessel #${payload.vesselId}`
                                : '—'),
                          ],
                          ...(postingEnabled
                            ? ([['Posting date', String(payload.postingDate ?? '—')]] as const)
                            : []),
                          [
                            'Currency',
                            currencies.find((c) => c.id === Number(payload.currencyId))
                              ? `${currencies.find((c) => c.id === Number(payload.currencyId))!.code}`
                              : String(payload.currencyCode ?? '—'),
                          ],
                          ['Description', String(payload.description ?? '—')],
                          ...extraInvoiceFields.map(([key, label]) => [label, payloadString(payload, key)] as const),
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className={
                              label === 'Description' || label === 'Creditor'
                                ? 'sm:col-span-2 lg:col-span-3 xl:col-span-2'
                                : undefined
                            }
                          >
                            <dt className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                              {label}
                            </dt>
                            <dd className={cn('mt-1 text-sm', colors.text)}>{value}</dd>
                          </div>
                        ))}
                      </dl>
                      {invoiceLines.length > 0 && (
                        <div>
                          <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                            Line items
                          </p>
                          <ul className={cn('mt-2 divide-y rounded-lg border', colors.border)}>
                            {invoiceLines.map((line, i) => (
                              <li key={i} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                                <span className={cn('min-w-0', colors.text)}>{line.description || '—'}</span>
                                {line.amount != null && (
                                  <span className={cn('shrink-0 tabular-nums', colors.text)}>
                                    {currencyLabel ? `${currencyLabel} ` : ''}
                                    {line.amount.toFixed(2)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* Journal panel — only when accounting is prepared */}
              {postingEnabled ? (
                <Card className="overflow-hidden rounded-2xl p-0 shadow-sm">
                  <Lines
                    lines={lines}
                    editable={draft}
                    colors={colors}
                    isDark={isDark}
                    companies={companies}
                    accounts={accounts}
                    currentStyle={currentStyle}
                    debitTotal={debitTotal}
                    creditTotal={creditTotal}
                    balanced={balanced}
                    onChange={changeLine}
                    onAdd={() => setLines([...lines, { role: 'EXPENSE', debitLocal: 0, creditLocal: 0, narrative: '' }])}
                    onRemove={(index) => setLines(lines.filter((_, i) => i !== index))}
                    onChooseStyle={draft ? applyLineStyle : undefined}
                  />
                </Card>
              ) : (
                draft && (
                  <Card className="overflow-hidden rounded-2xl p-5 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className={cn('text-sm font-semibold', colors.text)}>Accounting</h2>
                        <p className={cn('mt-1 text-xs', colors.textMuted)}>
                          Optional — prepare a journal only when this document should post to the ledger.
                          Scan and approval work without it.
                        </p>
                      </div>
                      <Button type="button" size="sm" variant="outline" loading={busy} disabled={busy} onClick={() => run(enablePosting)}>
                        Prepare accounting
                      </Button>
                    </div>
                  </Card>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Lines: React.FC<{
  lines: DocumentLine[];
  editable?: boolean;
  colors: ReturnType<typeof useTheme>['colors'];
  isDark?: boolean;
  companies: AccLookup[];
  accounts: AccLookup[];
  currentStyle?: TeachAmountStyle | null;
  debitTotal: number;
  creditTotal: number;
  balanced: boolean;
  onChange?: (index: number, key: keyof DocumentLine, value: string | number | null) => void;
  onAdd?: () => void;
  onRemove?: (index: number) => void;
  onChooseStyle?: (style: TeachAmountStyle) => void;
}> = ({
  lines,
  editable,
  colors,
  isDark = false,
  companies,
  accounts,
  currentStyle,
  debitTotal,
  creditTotal,
  balanced,
  onChange,
  onAdd,
  onRemove,
  onChooseStyle,
}) => {
  const [coaPickerLineIndex, setCoaPickerLineIndex] = useState<number | null>(null);
  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: c.id, label: companyLabel(c) })),
    [companies],
  );
  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: accountLabel(a) })),
    [accounts],
  );
  const roleSelectOptions = useMemo(
    () => ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label })),
    [],
  );

  const companyName = (lineId?: number | null) => {
    if (lineId == null) return '—';
    const c = companies.find((x) => x.id === lineId);
    return c ? companyLabel(c) : `#${lineId}`;
  };
  const accountName = (lineId?: number | null) => {
    if (lineId == null) return '—';
    const a = accounts.find((x) => x.id === lineId);
    return a ? accountLabel(a) : `#${lineId}`;
  };
  const coaPickerLine = coaPickerLineIndex != null ? lines[coaPickerLineIndex] ?? null : null;

  return (
    <div>
      <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4', colors.border)}>
        <div>
          <h2 className={cn('text-sm font-semibold', colors.text)}>Journal lines</h2>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
            {editable ? 'Choose a line type to rebuild — role accounts are kept when possible' : 'Posting lines'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums',
              balanced ? colors.success : colors.warning,
            )}
          >
            {balanced ? 'Balanced' : 'Out of balance'} · Dr {debitTotal.toFixed(2)} / Cr {creditTotal.toFixed(2)}
          </span>
          {editable && (
            <Button type="button" variant="outline" size="sm" onClick={onAdd}>
              <PlusIcon className="h-4 w-4" />
              Add
            </Button>
          )}
        </div>
      </div>

      {(editable || currentStyle) && (
        <div className={cn('border-b px-5 py-3', colors.border, colors.surface)}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                Posting style
              </p>
              <p className={cn('text-xs', colors.textMuted)}>
                How journal lines are built from invoice amounts
              </p>
            </div>
            <div
              className={cn(
                'grid w-full grid-cols-3 gap-0.5 rounded-xl border p-1 sm:max-w-xl sm:shrink-0',
                colors.border,
                colors.headerBg,
              )}
              role="tablist"
              aria-label="Posting line type"
            >
              {POSTING_STYLES.map((style) => {
                const active = currentStyle === style.value;
                return (
                  <button
                    key={style.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    disabled={!onChooseStyle}
                    title={style.hint}
                    onClick={() => onChooseStyle?.(style.value)}
                    className={cn(
                      'rounded-lg px-2.5 py-2 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40',
                      active
                        ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                        : cn(colors.textSecondary, onChooseStyle && colors.interactiveHover),
                      !onChooseStyle && 'cursor-default opacity-90',
                    )}
                  >
                    <span className="block text-xs font-semibold leading-tight">{style.label}</span>
                    <span
                      className={cn(
                        'mt-0.5 block truncate text-[10px] leading-tight',
                        active ? 'text-white/85' : colors.textMuted,
                      )}
                    >
                      {style.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div>
        {!lines.length ? (
          <div className={cn('px-5 py-10 text-center text-sm', colors.textMuted)}>
            No lines yet{editable ? ' — pick a line type above or add a row.' : '.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] table-fixed text-sm">
              <thead className={cn('border-b text-left text-[11px] uppercase tracking-wide', colors.border, colors.textMuted)}>
                <tr>
                  <th className="w-[8.5rem] px-5 py-2.5 font-medium">Type</th>
                  <th className="w-[12rem] px-3 py-2.5 font-medium">Company</th>
                  <th className="w-[18rem] px-3 py-2.5 font-medium">Account</th>
                  <th className="w-[6.5rem] px-3 py-2.5 text-right font-medium">Debit</th>
                  <th className="w-[6.5rem] px-3 py-2.5 text-right font-medium">Credit</th>
                  <th className="w-[10rem] px-3 py-2.5 font-medium">Narrative</th>
                  {editable && <th className="w-12 px-3 py-2.5" />}
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr
                    key={line.id ?? index}
                    className={cn(
                      'border-b last:border-b-0 transition',
                      colors.border,
                      colors.interactiveHover,
                    )}
                  >
                    <td className="max-w-[8.5rem] px-5 py-2.5 align-middle overflow-hidden">
                      {editable ? (
                        <Select
                          value={line.role ?? ''}
                          options={roleSelectOptions}
                          placeholder="Type"
                          showClearButton
                          onChange={(e) =>
                            onChange?.(
                              index,
                              'role',
                              e.target.value === '' ? null : (e.target.value as DocumentPostingRole),
                            )
                          }
                        />
                      ) : (
                        <span
                          className={cn(
                            'inline-flex max-w-full truncate rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                            roleBadgeClass(line.role, isDark),
                          )}
                          title={line.role || undefined}
                        >
                          {line.role || '—'}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[12rem] px-3 py-2.5 align-middle overflow-hidden">
                      {editable ? (
                        <Select
                          value={line.companyId ?? ''}
                          options={companyOptions}
                          placeholder="Company"
                          showClearButton
                          title={companyName(line.companyId)}
                          onChange={(e) =>
                            onChange?.(index, 'companyId', e.target.value === '' ? null : e.target.value)
                          }
                        />
                      ) : (
                        <span className={cn('block truncate', colors.text)} title={companyName(line.companyId)}>
                          {companyName(line.companyId)}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[18rem] px-3 py-2.5 align-middle overflow-hidden">
                      {editable ? (
                        <div className="flex items-center gap-1 min-w-0 max-w-full">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className={cn(
                              'h-10 w-10 shrink-0',
                              colors.primaryFg,
                              colors.primaryBorderAll,
                              colors.interactiveHover,
                            )}
                            title="Browse chart of accounts"
                            aria-label="Browse chart of accounts"
                            onClick={() => setCoaPickerLineIndex(index)}
                          >
                            <ListTreeIcon className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <Select
                              value={line.accountId ?? ''}
                              options={accountOptions}
                              placeholder="Account"
                              showClearButton
                              title={accountName(line.accountId)}
                              onChange={(e) =>
                                onChange?.(index, 'accountId', e.target.value === '' ? null : e.target.value)
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <span className={cn('block truncate', colors.text)} title={accountName(line.accountId)}>
                          {accountName(line.accountId)}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[6.5rem] px-3 py-2.5 text-right align-middle overflow-hidden">
                      {editable ? (
                        <NumberInput
                          value={line.debitLocal ?? 0}
                          allowDecimals
                          showClearButton={false}
                          className="text-right"
                          onChange={(e) => onChange?.(index, 'debitLocal', e.target.value)}
                        />
                      ) : (
                        <span className="tabular-nums">{Number(line.debitLocal ?? 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="max-w-[6.5rem] px-3 py-2.5 text-right align-middle overflow-hidden">
                      {editable ? (
                        <NumberInput
                          value={line.creditLocal ?? 0}
                          allowDecimals
                          showClearButton={false}
                          className="text-right"
                          onChange={(e) => onChange?.(index, 'creditLocal', e.target.value)}
                        />
                      ) : (
                        <span className="tabular-nums">{Number(line.creditLocal ?? 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="max-w-[10rem] px-3 py-2.5 align-middle overflow-hidden">
                      {editable ? (
                        <TextInput
                          value={line.narrative ?? ''}
                          showClearButton={false}
                          placeholder="Narrative"
                          title={line.narrative || undefined}
                          onChange={(e) => onChange?.(index, 'narrative', e.target.value)}
                        />
                      ) : (
                        <span className="block truncate" title={line.narrative || undefined}>
                          {line.narrative || '—'}
                        </span>
                      )}
                    </td>
                    {editable && (
                      <td className="w-12 px-3 py-2.5 align-middle">
                        <button
                          type="button"
                          title="Remove line"
                          onClick={() => onRemove?.(index)}
                          className={cn(
                            'inline-flex h-10 w-10 items-center justify-center rounded-lg transition',
                            colors.textMuted,
                            colors.interactiveHover,
                          )}
                        >
                          <TrashIcon className={cn('h-4 w-4', isDark ? 'text-red-400' : 'text-red-600')} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CoaAccountPickerModal
        isOpen={coaPickerLineIndex != null}
        onClose={() => setCoaPickerLineIndex(null)}
        value={coaPickerLine?.accountId ?? undefined}
        postingOnly
        onSelect={(accountId) => {
          if (coaPickerLineIndex == null) return;
          onChange?.(coaPickerLineIndex, 'accountId', accountId);
          setCoaPickerLineIndex(null);
        }}
      />
    </div>
  );
};
